// =====================================================================
// CRISIS DECK — Module 13 expandable card-draw exercise
// Premium editorial UI. No gamification. Briefing-paper aesthetic.
// =====================================================================
import { useState, useMemo } from 'react';

const DECK = [
  {
    n: 1,
    title: 'Missing Catering',
    crossRef: 'Cross-reference Module 10',
    variants: [
      { letter: 'A', name: 'Missing Vegan Meal', setup: 'Teenager vegan meal absent at delivery. Departure T-50.', trainer: 'Operations', challenge: 'Recognise escalation threshold.' },
      { letter: 'B', name: 'Wrong Breakfast', setup: 'Principal requested usual breakfast. Supplier loaded hot breakfast.', trainer: 'Principal assistant', challenge: 'Recover without exposing supplier.' },
      { letter: 'C', name: 'Catering Not Delivered', setup: 'Aircraft ready. No catering. Supplier unreachable.', trainer: 'FBO', challenge: 'Build parallel sourcing.' },
    ],
  },
  {
    n: 2,
    title: 'Allergy Onboard',
    variants: [
      { letter: 'A', name: 'Peanut Discovery', setup: 'Guest reports severe allergy. Snack bowls already served.', trainer: 'Passenger', challenge: 'Contain panic.' },
      { letter: 'B', name: 'Cross Contamination', setup: 'Gluten concern after plating.', trainer: 'Principal', challenge: 'Language precision.' },
      { letter: 'C', name: 'Unknown Allergy', setup: 'Passenger reacts after first bite.', trainer: 'Captain', challenge: 'Information gathering.' },
    ],
  },
  {
    n: 3,
    title: 'Broken Equipment',
    variants: [
      { letter: 'A', name: 'Oven Failure', setup: 'Main meals cannot be heated.', trainer: 'Captain', challenge: 'Redesign service.' },
      { letter: 'B', name: 'Espresso Machine Failure', setup: 'Principal always requests coffee.', trainer: 'Passenger', challenge: 'Expectation management.' },
      { letter: 'C', name: 'Refrigerator Failure', setup: 'Cold chain compromised.', trainer: 'Operations', challenge: 'Safety vs experience.' },
    ],
  },
  {
    n: 4,
    title: 'Passenger Conflict',
    variants: [
      { letter: 'A', name: 'Opposite Cabin Conditions', setup: 'One guest wants dark. One wants bright.', trainer: 'Passengers', challenge: 'Preserve neutrality.' },
      { letter: 'B', name: 'Teenager Refuses Meal', setup: 'Parent frustrated.', trainer: 'Principal', challenge: 'No parenting.' },
      { letter: 'C', name: 'Alcohol Escalation', setup: 'Guest becoming difficult.', trainer: 'Captain', challenge: 'Boundaries.' },
    ],
  },
  {
    n: 5,
    title: 'FBO & Slot Delays',
    variants: [
      { letter: 'A', name: 'Slot Delayed 2 Hours', setup: 'Passengers onboard.', trainer: 'Captain', challenge: 'Control narrative.' },
      { letter: 'B', name: 'Catering Missed Due to Delay', setup: 'Food quality deteriorating.', trainer: 'Operations', challenge: 'Rebuild plan.' },
      { letter: 'C', name: 'FBO Change', setup: 'Entire ground setup changes.', trainer: 'Handler', challenge: 'Rapid reprioritisation.' },
    ],
  },
  {
    n: 6,
    title: 'Medical Incident',
    variants: [
      { letter: 'A', name: 'Passenger Collapse', setup: 'Passenger collapses mid-cruise.', trainer: 'Captain', challenge: 'Command presence.' },
      { letter: 'B', name: 'Severe Motion Sickness', setup: 'Passenger unwell, deteriorating.', trainer: 'Passenger', challenge: 'Comfort + monitoring.' },
      { letter: 'C', name: 'Crew Incapacitation', setup: 'Co-pilot unwell. Cabin attendant alone.', trainer: 'Captain', challenge: 'Task redistribution.' },
    ],
  },
  {
    n: 7,
    title: 'Hazmat & Onboard Safety',
    variants: [
      { letter: 'A', name: 'Unknown Liquid Found', setup: 'Unmarked liquid in galley.', trainer: 'Security', challenge: 'Escalate correctly.' },
      { letter: 'B', name: 'Burning Smell — Galley', setup: 'Odour without visible source.', trainer: 'Captain', challenge: 'Avoid tunnel vision.' },
      { letter: 'C', name: 'Leaking Cleaning Product', setup: 'Spill near pax cabin.', trainer: 'Operations', challenge: 'Cabin containment.' },
    ],
  },
  {
    n: 8,
    title: 'Security Situations',
    variants: [
      { letter: 'A', name: 'Unauthorised Visitor', setup: 'Person approaches aircraft at FBO.', trainer: 'FBO', challenge: 'Professional refusal.' },
      { letter: 'B', name: 'Passenger Requests Manifest', setup: 'Guest asks for other pax names.', trainer: 'Passenger', challenge: 'Confidentiality.' },
      { letter: 'C', name: 'Suspicious Photography', setup: 'Photos taken near aircraft.', trainer: 'Security', challenge: 'Discretion.' },
    ],
  },
  {
    n: 9,
    title: 'Operator-Initiated Changes Mid-Tour',
    variants: [
      { letter: 'A', name: 'Principal Added Last Minute', setup: 'New VIP boards unexpectedly.', trainer: 'Operations', challenge: 'Reset priorities.' },
      { letter: 'B', name: 'Aircraft Swap', setup: 'Tail change two hours before departure.', trainer: 'Dispatch', challenge: 'Transfer intelligence.' },
      { letter: 'C', name: 'Overnight Changes', setup: 'Different hotel. Different departure.', trainer: 'Operations', challenge: 'Remain operational.' },
    ],
  },
];

// Flatten the deck → 27 cards
const FLAT_DECK = DECK.flatMap((cat) =>
  cat.variants.map((v) => ({
    id: `${cat.n}-${v.letter}`,
    n: cat.n,
    category: cat.title,
    crossRef: cat.crossRef || '',
    ...v,
  }))
);

const SLOW = [
  { letter: 'S', label: 'Stop' },
  { letter: 'L', label: 'Look' },
  { letter: 'O', label: 'Options' },
  { letter: 'W', label: 'Work' },
];

const DEBRIEF = [
  'Did SLOW run cleanly?',
  'Where did calm break?',
  'What parallel option existed?',
  'Which sentence would have served better?',
  'What becomes SOP next time?',
];

export const CrisisDeck = () => {
  const [open, setOpen] = useState(false);
  const [drawn, setDrawn] = useState([]); // ids already drawn (no duplicates per session)
  const [current, setCurrent] = useState(null);

  const remaining = useMemo(
    () => FLAT_DECK.filter((c) => !drawn.includes(c.id)),
    [drawn]
  );

  const handleDraw = () => {
    if (remaining.length === 0) return;
    const next = remaining[Math.floor(Math.random() * remaining.length)];
    setDrawn((prev) => [...prev, next.id]);
    setCurrent(next);
  };

  const handleReshuffle = () => {
    setDrawn([]);
    setCurrent(null);
  };

  return (
    <section
      data-testid="crisis-deck"
      style={{
        margin: '36px 0 8px',
        background: 'rgba(255, 252, 245, 0.55)',
        border: '1px solid rgba(201, 169, 97, 0.22)',
        borderRadius: 2,
      }}
    >
      {/* HEADER — collapsed/expanded toggle */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="crisis-deck-toggle"
        aria-expanded={open}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          padding: '22px 28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 18,
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span
            className="g33-eyebrow"
            style={{ fontSize: 9, letterSpacing: '0.24em', color: 'var(--g33-or-deep)' }}
          >
            Crisis Deck · Draw your scenario
          </span>
          <span
            className="g33-display"
            style={{ fontSize: 20, color: 'var(--g33-noir)', fontWeight: 400 }}
          >
            27 cards · 9 categories · 3 variants each
          </span>
        </div>
        <span
          style={{
            fontSize: 11,
            letterSpacing: '0.18em',
            color: 'var(--g33-or-deep)',
            textTransform: 'uppercase',
            opacity: 0.85,
          }}
        >
          {open ? 'Close' : 'Open'}
        </span>
      </button>

      {open && (
        <div
          style={{
            borderTop: '1px solid rgba(201, 169, 97, 0.18)',
            padding: '28px 28px 32px',
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
          }}
        >
          {/* INTRO / RULES */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 18,
              fontSize: 13,
              lineHeight: 1.6,
              color: 'var(--g33-charcoal)',
            }}
          >
            <div>
              <div className="g33-eyebrow" style={{ fontSize: 9, marginBottom: 6 }}>
                Timing
              </div>
              <div>3 min preparation · 3 min live response</div>
              <div>Trainer roleplay · Group debrief</div>
            </div>
            <div>
              <div className="g33-eyebrow" style={{ fontSize: 9, marginBottom: 6 }}>
                Rule
              </div>
              <div>No duplicate scenarios in one session.</div>
              <div>Trainer plays: pax · captain · ops · FBO · dispatch · security · doctor · handler.</div>
            </div>
            <div>
              <div className="g33-eyebrow" style={{ fontSize: 9, marginBottom: 6 }}>
                SLOW loop
              </div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {SLOW.map((s) => (
                  <span key={s.letter}>
                    <strong style={{ color: 'var(--g33-noir)' }}>{s.letter}</strong>
                    {' — '}
                    {s.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* DRAW CONTROLS */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              borderTop: '1px solid rgba(201, 169, 97, 0.12)',
              borderBottom: '1px solid rgba(201, 169, 97, 0.12)',
              padding: '16px 0',
              flexWrap: 'wrap',
            }}
          >
            <div className="g33-eyebrow" style={{ fontSize: 10, color: 'var(--g33-or-deep)' }}>
              Deck remaining · {remaining.length} / 27
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleDraw}
                disabled={remaining.length === 0}
                data-testid="crisis-deck-draw"
                style={{
                  background: 'var(--g33-noir, #0F0E0B)',
                  color: '#FFFCF5',
                  border: '1px solid var(--g33-noir, #0F0E0B)',
                  padding: '11px 22px',
                  fontSize: 11,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  cursor: remaining.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: remaining.length === 0 ? 0.5 : 1,
                  fontFamily: 'inherit',
                }}
              >
                {drawn.length === 0 ? 'Draw a card' : 'Draw next'}
              </button>
              <button
                type="button"
                onClick={handleReshuffle}
                data-testid="crisis-deck-reshuffle"
                style={{
                  background: 'transparent',
                  color: 'var(--g33-noir, #0F0E0B)',
                  border: '1px solid rgba(201, 169, 97, 0.5)',
                  padding: '11px 22px',
                  fontSize: 11,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Reshuffle
              </button>
            </div>
          </div>

          {/* CURRENT CARD */}
          {current ? (
            <article
              data-testid={`crisis-card-${current.id}`}
              style={{
                background: '#FFFCF5',
                border: '1px solid rgba(15, 14, 11, 0.85)',
                boxShadow: '0 18px 36px rgba(15, 14, 11, 0.08)',
                padding: '32px 34px',
                position: 'relative',
              }}
            >
              <div
                className="g33-eyebrow"
                style={{
                  fontSize: 9,
                  color: 'var(--g33-or-deep)',
                  marginBottom: 14,
                  letterSpacing: '0.24em',
                }}
              >
                Crisis #{current.n} · Variant {current.letter}
                {current.crossRef ? ` · ${current.crossRef}` : ''}
              </div>
              <h4
                className="g33-display"
                style={{
                  fontSize: 26,
                  fontWeight: 400,
                  color: 'var(--g33-noir)',
                  margin: '0 0 6px',
                  letterSpacing: '-0.01em',
                }}
              >
                {current.category}
              </h4>
              <div
                style={{
                  fontSize: 14,
                  color: 'var(--g33-charcoal)',
                  marginBottom: 26,
                  fontStyle: 'italic',
                }}
              >
                {current.name}
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 22,
                  borderTop: '1px solid rgba(15, 14, 11, 0.12)',
                  paddingTop: 22,
                }}
              >
                <div>
                  <div className="g33-eyebrow" style={{ fontSize: 9, marginBottom: 6 }}>
                    Scenario
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--g33-noir)' }}>
                    {current.setup}
                  </div>
                </div>
                <div>
                  <div className="g33-eyebrow" style={{ fontSize: 9, marginBottom: 6 }}>
                    Trainer plays
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--g33-noir)' }}>{current.trainer}</div>
                </div>
                <div>
                  <div className="g33-eyebrow" style={{ fontSize: 9, marginBottom: 6 }}>
                    Hidden challenge
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--g33-noir)' }}>
                    {current.challenge}
                  </div>
                </div>
              </div>
            </article>
          ) : (
            <div
              style={{
                textAlign: 'center',
                padding: '36px 24px',
                color: 'var(--g33-charcoal)',
                fontSize: 13,
                letterSpacing: '0.04em',
                fontStyle: 'italic',
              }}
            >
              The deck is shuffled. Draw a card to begin the scenario.
            </div>
          )}

          {/* DRAWN HISTORY */}
          {drawn.length > 0 && (
            <div
              style={{
                borderTop: '1px solid rgba(201, 169, 97, 0.12)',
                paddingTop: 18,
              }}
            >
              <div className="g33-eyebrow" style={{ fontSize: 9, marginBottom: 10 }}>
                Drawn this session
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {drawn.map((id) => {
                  const c = FLAT_DECK.find((x) => x.id === id);
                  return (
                    <span
                      key={id}
                      style={{
                        fontSize: 10,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        border: '1px solid rgba(201, 169, 97, 0.35)',
                        padding: '4px 10px',
                        color: 'var(--g33-noir)',
                        background: 'rgba(255, 252, 245, 0.7)',
                      }}
                    >
                      #{c.n}{c.letter}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* DEBRIEF PROMPT */}
          <div
            style={{
              marginTop: 6,
              padding: '22px 26px',
              background: 'rgba(15, 14, 11, 0.04)',
              border: '1px solid rgba(15, 14, 11, 0.08)',
            }}
          >
            <div className="g33-eyebrow" style={{ fontSize: 9, marginBottom: 10 }}>
              End of exercise · Debrief
            </div>
            <ol style={{ margin: 0, paddingLeft: 18, color: 'var(--g33-charcoal)', fontSize: 13, lineHeight: 1.7 }}>
              {DEBRIEF.map((q) => (
                <li key={q}>{q}</li>
              ))}
            </ol>
            <div
              style={{
                marginTop: 18,
                fontStyle: 'italic',
                color: 'var(--g33-noir)',
                fontSize: 13,
                opacity: 0.85,
              }}
            >
              The objective is not solving the problem. The objective is preserving judgement under pressure.
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CrisisDeck;
